import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { AmendedAction } from "models/order-management/amendedAction";
import {
  DropdownItem,
  OrderResponse,
  OrderTypeConfiguration,
} from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { OrderCreateRequestUpdate } from "../order-components-interface";

@Component({
  selector: "order-type",
  templateUrl: "order-type.html",
})
export class OrderTypeComponent {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderTypeConfigurations: Array<OrderTypeConfiguration> = [];
  @Input() order: OrderResponse;
  @Input() formType: string;
  @Input() orderFormState: OrderFormState;

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() amendedOrderRequestEvent = new EventEmitter<AmendedAction>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  orderTypeDropdownItems: Array<DropdownItem> = [];
  orderTypeValue: string;
  patientCareType: string;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    if (
      this.formType === "amendment" ||
      this.formType === "convertToOrder" ||
      this.formType ===
        "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare"
    )
      this.setAmendedValue();
    this.patientCareType = this.stateData.patientCareType.toString();
    this.setOrderTypeList(this.orderTypeConfigurations);
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  public setOrderTypeList(
    orderTypeConfigurations: Array<OrderTypeConfiguration>
  ): void {
    this.orderTypeDropdownItems = orderTypeConfigurations
      .filter((orderTypeConfiguration) => {
        const validation =
          orderTypeConfiguration.orderType &&
          orderTypeConfiguration.orderTypeDisplayName &&
          (orderTypeConfiguration.isDisabled == false ||
            orderTypeConfiguration.isDisabled === undefined);

        const patientCareType = this.patientCareType
          ? this.patientCareType.toUpperCase()
          : "";

        const careTypeFilter =
          !orderTypeConfiguration.careTypes ||
          orderTypeConfiguration.careTypes.includes(patientCareType);

        return validation && careTypeFilter;
      })
      .sort(
        (config1, config2) =>
          (config1.orderTypeSort ? config1.orderTypeSort : Infinity) -
          (config2.orderTypeSort ? config2.orderTypeSort : Infinity)
      )
      .flatMap((orderTypeConfiguration) => {
        if (
          orderTypeConfiguration.orderType === "MEDICATION" &&
          this.orderFormState?.isEPrescribeEnabled
        ) {
          return [
            {
              label: "Medication - non-eRx",
              value: orderTypeConfiguration.orderType,
            },
            {
              label: "Medication - eRx",
              value: `${orderTypeConfiguration.orderType}|ERX`,
            },
          ];
        }
        return [
          {
            label: orderTypeConfiguration.orderTypeDisplayName,
            value: orderTypeConfiguration.orderType,
          },
        ];
      });
  }

  setAmendedValue() {
    if (
      this.formType ===
      "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare"
    ) {
      this.orderTypeValue = "LEVEL_OF_CARE";
    } else {
      this.orderTypeValue = this.order.orderType;
    }
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onOrderTypeChange(event: DropdownItem) {
    this.orderCreateRequestEvent.emit({
      updates: { orderType: event.value },
    });
  }

  isDisabled() {
    return (
      this.formType === "amendment" ||
      this.formType === "convertToOrder" ||
      this.formType ===
        "openLevelOfCareAddOrderModalAfterCancellingPreviousLevelOfCare"
    );
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/order-type/order-type.ts